import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  TextField,
  Autocomplete,
  Grid,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";

export default function SalaryPayableBifurcatedDialog({
  open,
  onClose,
  startDate,
  endDate,
  data = [],
  loading = false,
  error = null,
}) {
  const [filteredData, setFilteredData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const gridRef = useRef();

  const formatIndianCurrency = (value) => {
    if (value == null || value === "") return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Date",
        field: "tranDate",
        sortable: true,
        filter: true,
        width: 130,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const d = new Date(params.value);
          return d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        headerName: "Voucher ID",
        field: "voucherDisplayId",
        sortable: true,
        filter: true,
        width: 160,
      },
      {
        headerName: "Particulars",
        field: "actledName",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: "Amount",
        field: "amount",
        sortable: true,
        filter: true,
        width: 150,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value != null ? formatIndianCurrency(Math.abs(params.value)) : "",
        cellStyle: (params) => {
          if (params.node.rowPinned === "bottom") {
            return { fontWeight: "bold", backgroundColor: "#f5f5f5", textAlign: "right" };
          }
          return { fontWeight: "bold", textAlign: "right" };
        },
      },
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 220,
      },
    ],
    []
  );

  const columnOptions = useMemo(
    () =>
      columnDefs
        .filter((col) => col.field)
        .map((col) => ({
          label: col.headerName,
          value: col.field,
          field: col.field,
          type: col.type || "string",
        })),
    [columnDefs]
  );

  // Auto-select first column on open
  useEffect(() => {
    if (open && columnOptions.length > 0) {
      setSearchColumn(columnOptions[0]);
    }
  }, [open]);

  // Sync data
  useEffect(() => {
    setFilteredData(data || []);
  }, [data]);

  // Filter logic
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredData(data || []);
      return;
    }
    const lower = searchText.toLowerCase();
    setFilteredData(
      (data || []).filter((row) => {
        const val = row[searchColumn.field];
        if (val == null) return false;
        if (typeof val === "number") return val.toString().includes(searchText);
        return val.toString().toLowerCase().includes(lower);
      })
    );
  }, [data, searchColumn, searchText]);

  // Footer totals
  const calculateFooter = useCallback((rows) => {
    const total = rows.reduce((sum, r) => sum + (r.amount != null ? Math.abs(r.amount) : 0), 0);
    return { actledName: "Total", amount: total, remarks: `${rows.length} entries` };
  }, []);

  useEffect(() => {
    setPinnedBottomRowData([calculateFooter(filteredData)]);
  }, [filteredData, calculateFooter]);

  const handleClearSearch = () => {
    setSearchColumn(columnOptions[0] || null);
    setSearchText("");
  };

  const handleDownloadExcel = () => {
    if (!filteredData || filteredData.length === 0) return;

    const exportData = filteredData.map((row) => ({
      Date: row.tranDate
        ? new Date(row.tranDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "",
      "Voucher ID": row.voucherDisplayId || "",
      Particulars: row.actledName || "",
      Amount: row.amount != null ? Math.abs(row.amount) : 0,
      Remarks: row.remarks || "",
    }));

    // Add total row
    const total = filteredData.reduce(
      (sum, r) => sum + (r.amount != null ? Math.abs(r.amount) : 0),
      0
    );
    exportData.push({
      Date: "",
      "Voucher ID": "",
      Particulars: "Total",
      Amount: total,
      Remarks: `${filteredData.length} entries`,
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Column widths
    ws["!cols"] = [
      { wch: 15 }, // Date
      { wch: 20 }, // Voucher ID
      { wch: 35 }, // Particulars
      { wch: 18 }, // Amount
      { wch: 55 }, // Remarks
    ];

    // Format amount column as number
    for (let r = 1; r <= exportData.length; r++) {
      const cell = XLSX.utils.encode_cell({ r, c: 3 });
      if (ws[cell] && typeof ws[cell].v === "number") {
        ws[cell].z = "#,##0.00";
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Salary_Payable");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Salary_Payable_Bifurcated_${startDate}_to_${endDate}.xlsx`
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#ffffff",
          borderRadius: 2,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          minHeight: "600px",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          bgcolor: "#282a2c",
          color: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#ffffff" }}>
          Salary Payable — Bifurcated Details
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#ffffff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent
        sx={{
          p: 3,
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "400px",
        }}
      >
        {/* Search Controls */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, flexShrink: 0, mt: 0.1 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={columnOptions}
              getOptionLabel={(option) => option.label}
              value={searchColumn}
              onChange={(_, val) => {
                setSearchColumn(val);
                if (!val) setSearchText("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Column"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Text"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={
                searchColumn ? `Search in ${searchColumn.label}...` : "Select a column first"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                disabled={!searchText}
                size="small"
                sx={{ color: "#252121" }}
              >
                Clear Search
              </Button>
              <Typography variant="caption" color="text.secondary">
                {searchText && searchColumn
                  ? `Showing ${filteredData.length} of ${data.length} rows`
                  : `Total ${data.length} rows`}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Date Range */}
        <Paper
          elevation={0}
          sx={{ mb: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1, flexShrink: 0 }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing data from <strong>{startDate}</strong> to <strong>{endDate}</strong>
          </Typography>
        </Paper>

        {/* Grid / States */}
        {loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}
          >
            <CircularProgress sx={{ color: "#f57c00" }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        ) : filteredData.length === 0 ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}
          >
            <Typography color="text.secondary">
              {searchText ? "No matching records found" : "No data available"}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: "300px",
              overflow: "hidden",
            }}
          >
            <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
              <AgGridReact
                ref={gridRef}
                rowData={filteredData}
                columnDefs={columnDefs}
                pinnedBottomRowData={pinnedBottomRowData}
                defaultColDef={{ resizable: true, filter: true, sortable: true }}
                getRowStyle={(params) => {
                  if (params.node.rowPinned === "bottom") {
                    return { fontWeight: "bold", backgroundColor: "#f5f5f5" };
                  }
                  return null;
                }}
                pagination={true}
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                headerHeight={48}
                rowHeight={44}
                suppressAggFuncInHeader={true}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
                onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
              />
            </div>
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}>
        <Button
          onClick={handleDownloadExcel}
          variant="contained"
          disabled={filteredData.length === 0}
          sx={{
            bgcolor: "#f57c00",
            "&:hover": { bgcolor: "#ffffff", color: "#f57c00", border: "1px solid #f57c00" },
            color: "#ffffff",
            mr: 1,
          }}
        >
          Download Excel
        </Button>
        <Button
          onClick={onClose}
          sx={{ color: "primary.main", "&:hover": { bgcolor: "rgba(255,152,0,0.1)" } }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: "#f57c00",
            "&:hover": { bgcolor: "#ffffff", color: "#f57c00", border: "1px solid #f57c00" },
            color: "#ffffff",
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SalaryPayableBifurcatedDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};
